(() => {
  const API_NAME = 'NordwegInstagramBentoLoader';
  const CONFIG_SELECTOR = 'script[data-instagram-bento-loader]';
  const GRID_SELECTOR = 'instagram-bento-grid';
  const OPENER_SELECTOR = '[data-instagram-bento-open]';

  if (window[API_NAME]?.refresh) {
    window[API_NAME].refresh();
    return;
  }

  const observedGrids = new WeakSet();
  const nearGrids = new Set();
  const dataQuery = window.matchMedia?.('(prefers-reduced-data: reduce)');
  const connection = typeof navigator !== 'undefined' ? navigator.connection : null;
  let observer = null;
  let runtimeUrl = '';
  let runtimePromise = null;

  function isReady() {
    return Boolean(customElements.get('instagram-bento-grid'));
  }

  function prefersReducedData() {
    return Boolean(dataQuery?.matches || connection?.saveData);
  }

  function configurations() {
    return [...document.querySelectorAll(CONFIG_SELECTOR)];
  }

  function currentConfig() {
    const configs = configurations();
    return configs[configs.length - 1] || null;
  }

  function load() {
    if (isReady()) return Promise.resolve(customElements.get('instagram-bento-grid'));
    if (runtimePromise) return runtimePromise;

    const config = currentConfig();
    runtimeUrl = config?.dataset.instagramBentoScript || runtimeUrl;
    if (!runtimeUrl) return Promise.reject(new Error('Instagram bento runtime URL is unavailable.'));

    runtimePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = runtimeUrl;
      script.async = true;
      script.dataset.instagramBentoRuntime = '';

      const settle = (event) => {
        script.removeEventListener('load', settle);
        script.removeEventListener('error', settle);
        if (event.type === 'error') {
          script.remove();
          reject(new Error(`Failed to load Instagram bento runtime: ${runtimeUrl}`));
          return;
        }
        if (!isReady()) {
          reject(new Error('Instagram bento runtime loaded without registering its element.'));
          return;
        }
        resolve(customElements.get('instagram-bento-grid'));
      };

      script.addEventListener('load', settle);
      script.addEventListener('error', settle);
      (document.head || document.documentElement).appendChild(script);
    }).then((constructor) => {
      observer?.disconnect();
      nearGrids.clear();
      return constructor;
    }).catch((error) => {
      runtimePromise = null;
      throw error;
    });

    return runtimePromise;
  }

  function loadFromProximity() {
    if (prefersReducedData()) return;
    load().catch(() => false);
  }

  function observeGrid(grid) {
    if (!grid || observedGrids.has(grid) || isReady()) return;
    observedGrids.add(grid);
    observer.observe(grid);
  }

  function refresh() {
    const configs = configurations();
    const latestConfig = configs[configs.length - 1];
    runtimeUrl = latestConfig?.dataset.instagramBentoScript || runtimeUrl;
    if (isReady() || configs.length === 0) return;

    if (configs.some((config) => config.dataset.designMode === 'true')) {
      load().catch(() => false);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      loadFromProximity();
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) nearGrids.add(entry.target);
          else nearGrids.delete(entry.target);
        });
        if (nearGrids.size > 0) loadFromProximity();
      }, { rootMargin: '1200px 0px', threshold: 0 });
    }

    configs.forEach((config) => observeGrid(document.getElementById(config.dataset.instagramBentoGridId)));
  }

  function gridFromTarget(target) {
    return target instanceof Element ? target.closest(GRID_SELECTOR) : null;
  }

  function warmFromIntent(event) {
    if (!gridFromTarget(event.target) || isReady()) return;
    load().catch(() => false);
  }

  function shouldReplayClick(event, opener) {
    if (event.defaultPrevented || event.button !== 0 || !opener.matches('a[href]')) return false;
    return !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  }

  function handleOpenerClick(event) {
    const opener = event.target instanceof Element ? event.target.closest(OPENER_SELECTOR) : null;
    if (!opener || !gridFromTarget(opener) || isReady() || !shouldReplayClick(event, opener)) return;

    event.preventDefault();
    const fallbackUrl = opener.href;
    load()
      .then(() => opener.click())
      .catch((error) => {
        console.error(error);
        if (fallbackUrl) window.location.assign(fallbackUrl);
      });
  }

  function handleDataPreferenceChange() {
    if (!prefersReducedData() && nearGrids.size > 0) loadFromProximity();
  }

  const api = { isReady, load, refresh };
  window[API_NAME] = api;

  document.addEventListener('pointerover', warmFromIntent, { capture: true, passive: true });
  document.addEventListener('pointerdown', warmFromIntent, { capture: true, passive: true });
  document.addEventListener('touchstart', warmFromIntent, { capture: true, passive: true });
  document.addEventListener('focusin', warmFromIntent, true);
  document.addEventListener('click', handleOpenerClick, true);
  document.addEventListener('shopify:section:load', refresh);
  dataQuery?.addEventListener('change', handleDataPreferenceChange);
  connection?.addEventListener?.('change', handleDataPreferenceChange);

  refresh();
})();
